import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { MessageCircleQuestion, Bug, Lightbulb, ThumbsUp, HelpCircle, X, Loader2, Check } from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const KIND_OPTIONS = [
  { key: 'bug',         label: 'Bug',         icon: Bug,        accent: 'border-red-300 text-red-700 bg-red-50' },
  { key: 'suggestion',  label: 'Idea',        icon: Lightbulb,  accent: 'border-amber-300 text-amber-700 bg-amber-50' },
  { key: 'compliment',  label: 'Compliment',  icon: ThumbsUp,   accent: 'border-green-300 text-green-700 bg-green-50' },
  { key: 'question',    label: 'Question',    icon: HelpCircle, accent: 'border-blue-300 text-blue-700 bg-blue-50' },
];

const SEVERITIES = ['low', 'medium', 'high'];

/**
 * FeedbackWidget — global floating button that opens a small report modal.
 * Positioned to avoid:
 *   - the BottomNav on mobile (clears it via bottom-24)
 *   - the Emergent badge on desktop (sits above it via lg:bottom-20)
 *   - the sidebar/logout area on desktop (anchored right, not left)
 */
const FeedbackWidget = () => {
  const [open, setOpen] = useState(false);
  const [kind, setKind] = useState('bug');
  const [message, setMessage] = useState('');
  const [severity, setSeverity] = useState('medium');
  const [contactEmail, setContactEmail] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [done, setDone] = useState(false);
  const [err, setErr] = useState('');
  const [hasUser, setHasUser] = useState(true);

  useEffect(() => {
    if (!open) return;
    // Best-effort: detect if we're anonymous so we know to ask for an email
    (async () => {
      try {
        await axios.get(`${API}/auth/me`, { withCredentials: true });
        setHasUser(true);
      } catch { setHasUser(false); }
    })();
  }, [open]);

  const reset = () => {
    setKind('bug'); setMessage(''); setSeverity('medium');
    setContactEmail(''); setSubmitting(false); setDone(false); setErr('');
  };

  const submit = async (e) => {
    e?.preventDefault?.();
    if (!message.trim()) {
      setErr('Please tell us what you observed.');
      return;
    }
    setSubmitting(true);
    setErr('');
    try {
      await axios.post(`${API}/feedback`, {
        kind,
        message: message.trim(),
        page_url: typeof window !== 'undefined' ? window.location.pathname + window.location.search : null,
        severity: kind === 'bug' ? severity : null,
        user_agent: typeof navigator !== 'undefined' ? navigator.userAgent : null,
        viewport: typeof window !== 'undefined' ? `${window.innerWidth}x${window.innerHeight}` : null,
        console_error_count: typeof window !== 'undefined' && window.__civilxErrorCount ? window.__civilxErrorCount : 0,
        contact_email: contactEmail.trim() || null,
      }, { withCredentials: true });
      setDone(true);
    } catch (e2) {
      setErr(e2.response?.data?.detail || 'Could not send — please try again.');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <>
      <button
        type="button"
        onClick={() => { reset(); setOpen(true); }}
        data-testid="feedback-fab"
        title="Send feedback"
        aria-label="Send feedback"
        className="fixed bottom-24 right-4 lg:bottom-20 lg:right-6 z-30 w-12 h-12 rounded-full bg-[#002FA7] text-white shadow-lg hover:bg-[#00247D] active:scale-95 transition-all flex items-center justify-center"
      >
        <MessageCircleQuestion className="w-5 h-5" strokeWidth={2.25} />
      </button>

      {open && (
        <div className="fixed inset-0 z-50 flex items-end sm:items-center justify-center p-4 bg-slate-900/40" onClick={() => setOpen(false)} data-testid="feedback-modal">
          <div
            onClick={(e) => e.stopPropagation()}
            className="bg-white rounded-md w-full sm:max-w-md max-h-[85vh] overflow-y-auto shadow-2xl"
          >
            <div className="px-5 py-4 border-b border-slate-100 flex items-center justify-between">
              <div>
                <h2 className="text-base font-bold text-slate-900" style={{ fontFamily: 'Work Sans, sans-serif' }}>
                  {done ? 'Thanks!' : 'Send us feedback'}
                </h2>
                <p className="text-xs text-slate-500" style={{ fontFamily: 'IBM Plex Sans, sans-serif' }}>
                  {done ? "We've recorded your note." : "Tell us what's working or what's not."}
                </p>
              </div>
              <button onClick={() => setOpen(false)} className="p-1 text-slate-400 hover:text-slate-700" data-testid="feedback-close" aria-label="Close">
                <X className="w-4 h-4" />
              </button>
            </div>

            {done ? (
              <div className="p-8 text-center" data-testid="feedback-success">
                <div className="w-14 h-14 rounded-full bg-green-100 flex items-center justify-center mx-auto mb-4">
                  <Check className="w-7 h-7 text-green-600" strokeWidth={2.5} />
                </div>
                <p className="text-sm text-slate-700" style={{ fontFamily: 'IBM Plex Sans, sans-serif' }}>Your feedback is in. We read every one.</p>
                <button
                  onClick={() => setOpen(false)}
                  data-testid="feedback-done"
                  className="mt-4 px-4 py-2 text-sm font-semibold text-[#002FA7] hover:bg-slate-50 rounded-md"
                  style={{ fontFamily: 'IBM Plex Sans, sans-serif' }}
                >
                  Close
                </button>
              </div>
            ) : (
              <form onSubmit={submit} className="p-5 space-y-4" style={{ fontFamily: 'IBM Plex Sans, sans-serif' }}>
                <div>
                  <label className="block text-[10px] font-bold uppercase tracking-widest text-slate-500 mb-2">Category</label>
                  <div className="grid grid-cols-4 gap-2">
                    {KIND_OPTIONS.map((opt) => {
                      const Icon = opt.icon;
                      const active = kind === opt.key;
                      return (
                        <button
                          key={opt.key}
                          type="button"
                          data-testid={`feedback-kind-${opt.key}`}
                          onClick={() => setKind(opt.key)}
                          className={`flex flex-col items-center gap-1 py-2 px-1 rounded-md text-[11px] font-semibold border transition-all ${active ? opt.accent : 'border-slate-200 text-slate-500 hover:border-slate-300'}`}
                        >
                          <Icon className="w-4 h-4" strokeWidth={2.25} />
                          {opt.label}
                        </button>
                      );
                    })}
                  </div>
                </div>

                {kind === 'bug' && (
                  <div>
                    <label className="block text-[10px] font-bold uppercase tracking-widest text-slate-500 mb-1.5">Severity</label>
                    <div className="flex gap-2">
                      {SEVERITIES.map((s) => (
                        <button
                          key={s}
                          type="button"
                          data-testid={`feedback-sev-${s}`}
                          onClick={() => setSeverity(s)}
                          className={`flex-1 px-2 py-1.5 text-xs font-semibold rounded border transition-all capitalize ${severity === s ? 'bg-[#002FA7] text-white border-[#002FA7]' : 'bg-white border-slate-300 text-slate-700 hover:border-slate-400'}`}
                        >
                          {s}
                        </button>
                      ))}
                    </div>
                  </div>
                )}

                <div>
                  <label className="block text-[10px] font-bold uppercase tracking-widest text-slate-500 mb-1.5">
                    {kind === 'bug' ? 'What went wrong?' : 'Your message'}
                  </label>
                  <textarea
                    value={message}
                    onChange={(e) => setMessage(e.target.value)}
                    placeholder={kind === 'bug' ? 'Steps to reproduce, expected vs. actual…' : 'Tell us more…'}
                    rows={4}
                    autoFocus
                    data-testid="feedback-message"
                    className="w-full px-3 py-2 text-sm border border-slate-300 rounded-md focus:ring-2 focus:ring-[#002FA7] focus:border-transparent focus:outline-none resize-none"
                  />
                  <p className="text-[10px] text-slate-400 mt-1">
                    We auto-attach the page URL, viewport size, and your browser info.
                  </p>
                </div>

                {!hasUser && (
                  <div>
                    <label className="block text-[10px] font-bold uppercase tracking-widest text-slate-500 mb-1.5">Email (optional)</label>
                    <input
                      type="email"
                      value={contactEmail}
                      onChange={(e) => setContactEmail(e.target.value)}
                      placeholder="so we can follow up"
                      data-testid="feedback-email"
                      className="w-full px-3 py-2 text-sm border border-slate-300 rounded-md focus:ring-2 focus:ring-[#002FA7] focus:border-transparent focus:outline-none"
                    />
                  </div>
                )}

                {err && (
                  <div className="text-xs text-red-700 bg-red-50 border border-red-200 rounded p-2" role="alert" data-testid="feedback-error">
                    {err}
                  </div>
                )}

                <div className="flex items-center justify-end gap-2 pt-2">
                  <button type="button" onClick={() => setOpen(false)} className="px-3 py-2 text-sm text-slate-600 hover:text-slate-900 font-semibold">
                    Cancel
                  </button>
                  <button
                    type="submit"
                    disabled={submitting || !message.trim()}
                    data-testid="feedback-submit"
                    className="inline-flex items-center gap-2 bg-[#002FA7] text-white px-4 py-2 text-sm font-semibold rounded-md hover:bg-[#00247D] disabled:opacity-50"
                  >
                    {submitting ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : null}
                    Send
                  </button>
                </div>
              </form>
            )}
          </div>
        </div>
      )}
    </>
  );
};

export default FeedbackWidget;
